// Signature motion (docs/04-design.md §1), via Framer Motion. MotionConfig reducedMotion="user"
// turns transforms off for prefers-reduced-motion users, leaving instant opacity changes only.
import { animate, LazyMotion, m, MotionConfig, useMotionValue, useReducedMotion, useTransform } from 'framer-motion';
import { useEffect, type ReactNode } from 'react';

export const SPRING = { type: 'spring', stiffness: 400, damping: 40 } as const;

// Animation features load lazily so the landing bundle only carries the tiny `m` core.
const loadFeatures = () => import('./motion-features').then((x) => x.default);

export function MotionProvider({ children }: { children: ReactNode }) {
  return (
    <LazyMotion features={loadFeatures} strict>
      <MotionConfig reducedMotion="user">{children}</MotionConfig>
    </LazyMotion>
  );
}

/** Card entry: a short "slide in from the ruler". Stagger by index, capped so long lists stay fast. */
export function EnterItem({ index = 0, children, className }: { index?: number; children: ReactNode; className?: string }) {
  return (
    <m.div
      className={className}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...SPRING, delay: Math.min(index, 8) * 0.03 }}
    >
      {children}
    </m.div>
  );
}

/** Approval stamp: lands with a small press (scale + settle to the -2deg stamp angle). */
export function StampIn({ children }: { children: ReactNode }) {
  return (
    <m.span
      className="inline-flex"
      initial={{ opacity: 0, scale: 1.6, rotate: -9 }}
      animate={{ opacity: 1, scale: 1, rotate: 0 }}
      transition={{ type: 'spring', stiffness: 520, damping: 26 }}
    >
      {children}
    </m.span>
  );
}

/** Number ticker: tweens to `value` in <= 600ms; instant under reduced motion. */
export function Ticker({ value, format = (n: number) => String(Math.round(n)) }: { value: number; format?: (n: number) => string }) {
  const reduced = useReducedMotion();
  const mv = useMotionValue(reduced ? value : 0);
  const text = useTransform(mv, (v) => format(v));
  useEffect(() => {
    if (reduced) {
      mv.set(value);
      return;
    }
    const c = animate(mv, value, { duration: 0.6, ease: [0.2, 0.8, 0.2, 1] });
    return () => c.stop();
  }, [value, reduced, mv]);
  return <m.span>{text}</m.span>;
}
